import axios from 'axios'

import type { DocumentNode } from 'graphql'

interface AdminErrorResponse {
  message?: string
}

const getAdminErrorMessage = (error: unknown): string | null => {
  if (!axios.isAxiosError<AdminErrorResponse>(error)) {
    return error instanceof Error ? error.message : null
  }

  const message = error.response?.data?.message
  return typeof message === 'string' && message.trim() !== '' ? message : error.message
}

/**
 * Extract query string from DocumentNode or return string as-is
 */
const getQueryString = (query: string | DocumentNode): string => {
  if (typeof query === 'string') {
    return query
  }
  // DocumentNode from .graphql files
  const source = query.loc?.source.body
  if (!source) {
    throw new Error('Invalid GraphQL query: missing source')
  }
  return source
}

/**
 * GraphQL request helper
 */
export const request = <TData = unknown>(
  query: string | DocumentNode,
  variables: Record<string, unknown> = {}
): Promise<{ data: TData }> => {
  return axios
    .post('/graphql', { query: getQueryString(query), variables })
    .then((response) => response.data as { data: TData })
}

/**
 * Admin controller request helper (with CSRF form_key)
 */
export const adminRequest = async <TData = unknown>(
  url: string,
  data: Record<string, unknown> = {}
): Promise<TData> => {
  const formKey = window.FORM_KEY
  if (!formKey) {
    throw new Error('Form key not available')
  }

  const formData = new FormData()
  formData.append('form_key', formKey)

  for (const [key, value] of Object.entries(data)) {
    if (value === null || value === undefined) {
      continue
    }
    if (typeof value === 'object') {
      formData.append(key, JSON.stringify(value))
    } else {
      // eslint-disable-next-line @typescript-eslint/no-base-to-string -- value is primitive here
      formData.append(key, String(value))
    }
  }

  try {
    const response = await axios.post(url, formData, {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    })

    return response.data as TData
  } catch (error: unknown) {
    throw new Error(getAdminErrorMessage(error) ?? 'Admin request failed')
  }
}
